import { useEffect, useState } from 'react';
import { aiSectionPrompts,
         type AiSectionPromptRow, type AiTypeWithSections } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

/**
 * Admin: edycja promptów per (typ opracowania, sekcja).
 *
 * Treść każdego promptu jest doklejana do promptu generującego draft AI dla danej sekcji
 * w danym typie opracowania (patrz {@code AiService.budujPrompt}). Admin może dodać/zmienić/
 * usunąć prompt — pusty prompt = nic nie doklejamy.
 *
 * Mechanika:
 * 1. Wybór typu opracowania w dropdownie → pobranie z backendu listy sekcji + zdefiniowanych promptów
 * 2. Każda sekcja ma textarea + checkbox aktywny — edycja lokalna w stanie `draft`
 * 3. „Zapisz wszystkie" → bulk-upsert: każda sekcja jest wstawiana/aktualizowana, pusta sekcja
 *    z istniejącym wpisem zostaje USUNIĘTA (efektywnie odłącza prompt od sekcji)
 */
export function AiSectionPromptsAdmin() {
  const [types, setTypes] = useState<AiTypeWithSections[]>([]);
  const [studyType, setStudyType] = useState<string>('');
  const [rows, setRows] = useState<AiSectionPromptRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, { promptText: string; active: boolean }>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    aiSectionPrompts.types()
      .then(t => {
        setTypes(t);
        if (t.length > 0 && !studyType) setStudyType(t[0].studyType);
      })
      .catch(e => setMsg({ type: 'err', text: String(e) }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadForType = (st: string) => {
    if (!st) return;
    setLoading(true); setMsg(null);
    aiSectionPrompts.list(st)
      .then(r => {
        setRows(r.rows);
        const d: Record<string, { promptText: string; active: boolean }> = {};
        r.rows.forEach(row => { d[row.section] = { promptText: row.promptText, active: row.active }; });
        setDraft(d);
      })
      .catch(e => setMsg({ type: 'err', text: String(e) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (studyType) loadForType(studyType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyType]);

  const setField = (section: string, patch: Partial<{ promptText: string; active: boolean }>) => {
    setDraft(prev => ({
      ...prev,
      [section]: { ...prev[section], ...patch } as { promptText: string; active: boolean },
    }));
  };

  const isDirty = (() => {
    if (!rows) return false;
    return rows.some(r => {
      const d = draft[r.section];
      if (!d) return false;
      return d.promptText !== r.promptText || d.active !== r.active;
    });
  })();

  const onSave = async () => {
    if (!rows) return;
    setBusy(true); setMsg(null);
    try {
      const payload = rows.map(r => ({
        section: r.section,
        promptText: draft[r.section]?.promptText ?? '',
        active: draft[r.section]?.active ?? true,
      }));
      const res = await aiSectionPrompts.bulkUpsert(studyType, payload);
      setMsg({
        type: 'ok',
        text: `Zapisano: ${res.upserted} ${res.upserted === 1 ? 'prompt zaktualizowany' : 'promptów zaktualizowanych'}`
            + (res.deleted > 0 ? `, ${res.deleted} usunięto` : ''),
      });
      loadForType(studyType);
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message ?? String(e) });
    } finally {
      setBusy(false);
    }
  };

  const onDeleteOrphan = async (id: number, section: string) => {
    if (!confirm(`Usunąć prompt dla sekcji „${section}"? Jest to wpis spoza aktualnej listy sekcji typu.`)) return;
    setBusy(true);
    try {
      await aiSectionPrompts.delete(id);
      loadForType(studyType);
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message ?? String(e) });
    } finally {
      setBusy(false);
    }
  };

  const currentType = types.find(t => t.studyType === studyType);

  return (
    <div className="max-w-[1200px] mx-auto">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Prompty AI per sekcja</h1>
          <div className="text-ink-400 text-[13px]">
            Zdefiniuj dodatkowe wskazówki merytoryczne doklejane do promptu generującego draft AI
            dla konkretnej sekcji w danym typie opracowania.
          </div>
        </div>
      </header>

      {msg && (
        <div className={'mb-4 p-3 rounded-md text-[12px] whitespace-pre-wrap font-mono ' +
          (msg.type === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
          {msg.text}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body flex items-end gap-3 flex-wrap">
          <label className="field !flex-none w-[400px]">
            <span className="field-label">Typ opracowania</span>
            <SearchableSelect value={studyType} onChange={setStudyType}>
              {types.map(t => (
                <option key={t.studyType} value={t.studyType}>
                  {t.studyTypeAbbr}-{t.studyTypeCode} — {t.studyTypeName}
                </option>
              ))}
            </SearchableSelect>
          </label>
          {currentType && (
            <div className="text-[11px] text-ink-500 mb-1">
              {currentType.sections.length} {currentType.sections.length === 1 ? 'sekcja' : 'sekcji'} w tym typie
            </div>
          )}
          <div className="flex-1"/>
          <button onClick={onSave} disabled={busy || !isDirty}
                  className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/>
            {busy ? 'Zapisuję…' : 'Zapisz wszystkie'}
          </button>
        </div>
      </div>

      <div className="text-[12px] text-ink-600 mb-4 p-3 bg-ai-500/5 border border-ai-500/20 rounded">
        <div className="font-semibold text-ai-700 mb-0.5">Jak to działa</div>
        Treść każdego promptu zostaje doklejona do promptu generującego draft AI dla DOKŁADNIE TEJ sekcji
        w DOKŁADNIE TYM typie opracowania. Pozwala wymusić specyficzne wymagania merytoryczne (podstawy
        prawne, wzory, klasyfikacje) bez modyfikowania kodu. Pusty prompt = nic nie doklejamy.
        Zmiany działają od kolejnej generacji draftu.
      </div>

      {loading && <div className="text-ink-400">Ładowanie…</div>}

      {!loading && rows && rows.length === 0 && (
        <div className="text-ink-400 text-[13px] italic p-4">
          Brak skonfigurowanych sekcji dla tego typu opracowania.
        </div>
      )}

      {!loading && rows && rows.length > 0 && (
        <div className="space-y-4">
          {rows.map(row => {
            const d = draft[row.section] ?? { promptText: row.promptText, active: row.active };
            const dirty = d.promptText !== row.promptText || d.active !== row.active;
            return (
              <div key={row.section}
                   className={'card ' + (dirty ? 'border-ai-500/40' : '')}>
                <div className="card-head">
                  <div className="card-title">{row.section}</div>
                  {!row.knownInTemplate && (
                    <span className="ml-2 pill pill-amber text-[10px]"
                          title="Sekcja nieobecna w aktualnej liście sekcji typu — wpis-sierota">
                      ⚠ poza szablonem
                    </span>
                  )}
                  {row.id != null && (
                    <span className="ml-2 text-[10px] text-ink-400 font-mono">#{row.id}</span>
                  )}
                  {dirty && <span className="ml-2 text-[10px] text-ai-700 font-semibold">(zmienione)</span>}
                  <div className="ml-auto flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-[11px] text-ink-700 cursor-pointer">
                      <input type="checkbox" checked={d.active}
                             onChange={e => setField(row.section, { active: e.target.checked })}/>
                      Aktywny
                    </label>
                    {row.id != null && !row.knownInTemplate && (
                      <button onClick={() => onDeleteOrphan(row.id!, row.section)}
                              disabled={busy}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Usuń wpis-sierota">
                        <Icon name="x" className="w-3.5 h-3.5"/>
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  <textarea
                    value={d.promptText}
                    onChange={e => setField(row.section, { promptText: e.target.value })}
                    rows={5}
                    placeholder='np. „Wymień konkretne paragrafy ustawy. Stosuj klasyfikację I-V wg Rozp. 2019/2148. Nie używaj określeń niepewnych typu „prawdopodobnie"."'
                    className="input w-full text-[12px] font-mono"
                  />
                  {row.updatedAt && (
                    <div className="text-[10px] text-ink-400 mt-1">
                      Ostatnia zmiana: {new Date(row.updatedAt).toLocaleString('pl-PL')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
